import { ChevronDown } from "@untitledui/icons";
import type { FC, ReactNode, Ref, RefAttributes } from "react";
import { createContext, isValidElement } from "react";
import type { SelectProps as AriaSelectProps } from "react-aria-components";
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  ListBox as AriaListBox,
  Select as AriaSelect,
  SelectValue as AriaSelectValue,
} from "react-aria-components";
import { Avatar } from "@/components/base/avatar/avatar";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
import { ComboBox } from "./combobox";
import { Popover } from "./popover";
import { SelectItem } from "./select-item";

export type SelectItemType = {
  id: string;
  label?: string;
  avatarUrl?: string;
  isDisabled?: boolean;
  supportingText?: string;
  icon?: FC | ReactNode;
};

export interface CommonProps {
  hint?: string;
  label?: string;
  tooltip?: string;
  size?: "sm" | "md";
  placeholder?: string;
  pill?: boolean;
  /** Hide each item's supportingText everywhere except a tooltip on the selected value. */
  hideSupportingText?: boolean;
  stackSupportingText?: boolean;
}

interface SelectProps
  extends Omit<AriaSelectProps<SelectItemType>, "children" | "items">,
    RefAttributes<HTMLDivElement>,
    CommonProps {
  items?: SelectItemType[];
  popoverClassName?: string;
  placeholderIcon?: FC | ReactNode;
  children: ReactNode | ((item: SelectItemType) => ReactNode);
  renderEmptyState?: () => ReactNode;
}

interface SelectValueProps {
  isOpen: boolean;
  size: "sm" | "md";
  isFocused: boolean;
  isDisabled: boolean;
  placeholder?: string;
  pill?: boolean;
  ref?: Ref<HTMLButtonElement>;
  placeholderIcon?: FC | ReactNode;
  hideSupportingText?: boolean;
  stackSupportingText?: boolean;
}

export const sizes = {
  sm: { root: "py-2 px-3", shortcut: "pr-2.5" },
  md: { root: "py-2.5 px-3.5", shortcut: "pr-3" },
};

const pillSize = { root: "py-1 px-2.5", shortcut: "pr-2.5" };

const triggerClassName = ({ pill, isActive, isDisabled }: { pill?: boolean; isActive: boolean; isDisabled: boolean }) =>
  cx(
    "relative flex w-full cursor-pointer items-center shadow-none outline-hidden transition duration-100 ease-linear",
    pill ? "rounded-full bg-brand-100 ring-0" : "rounded-lg bg-primary ring-1 ring-primary ring-inset",
    isActive && "ring-2 ring-brand",
    isDisabled && "cursor-not-allowed bg-disabled_subtle text-disabled",
  );

const TriggerChevron = ({ size, pill }: { size: "sm" | "md"; pill?: boolean }) => (
  <ChevronDown
    aria-hidden="true"
    className={cx(
      "ml-auto shrink-0",
      pill ? "size-3.5 stroke-[2.5px] text-brand-primary" : "text-fg-quaternary",
      !pill && (size === "sm" ? "size-4 stroke-[2.5px]" : "size-5"),
    )}
  />
);

const SelectValue = ({
  isOpen,
  isFocused,
  isDisabled,
  size,
  placeholder,
  placeholderIcon,
  pill,
  ref,
  hideSupportingText,
  stackSupportingText,
}: SelectValueProps) => {
  return (
    <AriaButton ref={ref} className={triggerClassName({ pill, isActive: isFocused || isOpen, isDisabled })}>
      <AriaSelectValue<SelectItemType>
        className={cx(
          "flex h-max w-full items-center justify-start gap-2 truncate text-left align-middle",

          // Icon styles
          pill
            ? "*:data-icon:size-4 *:data-icon:shrink-0 *:data-icon:text-brand-primary"
            : "*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary in-disabled:*:data-icon:text-fg-disabled",

          pill ? pillSize.root : sizes[size].root,
        )}
      >
        {(state) => {
          const Icon = state.selectedItem?.icon || placeholderIcon;
          return (
            <>
              {state.selectedItem?.avatarUrl ? (
                <Avatar size="xs" src={state.selectedItem.avatarUrl} alt={state.selectedItem.label} />
              ) : isReactComponent(Icon) ? (
                <Icon data-icon aria-hidden="true" />
              ) : isValidElement(Icon) ? (
                Icon
              ) : null}

              {state.selectedItem ? (
                hideSupportingText && state.selectedItem.supportingText ? (
                  <Tooltip title={state.selectedItem.supportingText} delay={500}>
                    <TooltipTrigger className="inline-flex w-full min-w-0">
                      <p
                        className={cx(
                          "truncate",
                          pill ? "text-xs font-semibold text-brand-primary" : "text-md font-medium text-primary",
                        )}
                      >
                        {state.selectedItem?.label}
                      </p>
                    </TooltipTrigger>
                  </Tooltip>
                ) : (
                  <section className="flex w-full gap-2 min-w-0">
                    <p
                      className={cx(
                        "truncate",
                        pill ? "text-xs font-semibold text-brand-primary" : "text-md font-medium text-primary",
                      )}
                    >
                      {state.selectedItem?.label}
                    </p>
                    {!hideSupportingText && !stackSupportingText && state.selectedItem?.supportingText && (
                      <p className="truncate text-md text-tertiary">{state.selectedItem?.supportingText}</p>
                    )}
                  </section>
                )
              ) : (
                <p className={cx("text-md text-placeholder", isDisabled && "text-disabled")}>{placeholder}</p>
              )}

              <TriggerChevron size={size} pill={pill} />
            </>
          );
        }}
      </AriaSelectValue>
    </AriaButton>
  );
};

export const SelectContext = createContext<{
  size: "sm" | "md";
  hideSupportingText?: boolean;
  stackSupportingText?: boolean;
}>({ size: "sm" });

const Select = ({
  placeholder = "Select",
  placeholderIcon,
  size = "sm",
  pill,
  children,
  items,
  label,
  hint,
  tooltip,
  hideSupportingText,
  stackSupportingText,
  className,
  renderEmptyState,
  ...rest
}: SelectProps) => {
  if (renderEmptyState && items?.length === 0) {
    return (
      <div className={cx("flex flex-col gap-1.5", typeof className === "string" && className)}>
        {label && <Label tooltip={tooltip}>{label}</Label>}

        <AriaDialogTrigger>
          <AriaButton
            isDisabled={rest.isDisabled}
            className={({ isFocusVisible, isPressed, isDisabled }) =>
              triggerClassName({ pill, isActive: isFocusVisible || isPressed, isDisabled })
            }
          >
            <span
              className={cx(
                "flex h-max w-full items-center justify-start gap-2 truncate text-left",
                pill ? pillSize.root : sizes[size].root,
              )}
            >
              <span className={cx("text-md text-placeholder", rest.isDisabled && "text-disabled")}>{placeholder}</span>
              <TriggerChevron size={size} pill={pill} />
            </span>
          </AriaButton>

          <Popover size={size} className={rest.popoverClassName}>
            <AriaDialog className="outline-hidden">{renderEmptyState()}</AriaDialog>
          </Popover>
        </AriaDialogTrigger>

        {hint && <HintText>{hint}</HintText>}
      </div>
    );
  }

  return (
    <SelectContext.Provider value={{ size, hideSupportingText, stackSupportingText }}>
      <AriaSelect
        {...rest}
        className={(state) =>
          cx("flex flex-col gap-1.5", typeof className === "function" ? className(state) : className)
        }
      >
        {(state) => (
          <>
            {label && (
              <Label isRequired={state.isRequired} tooltip={tooltip}>
                {label}
              </Label>
            )}

            <SelectValue
              {...state}
              {...{ size, placeholder, pill, hideSupportingText, stackSupportingText }}
              placeholderIcon={placeholderIcon}
            />

            <Popover size={size} hasItems={!!items} className={rest.popoverClassName}>
              <AriaListBox items={items} className="size-full outline-hidden">
                {children}
              </AriaListBox>
            </Popover>

            {hint && <HintText isInvalid={state.isInvalid}>{hint}</HintText>}
          </>
        )}
      </AriaSelect>
    </SelectContext.Provider>
  );
};

const _Select = Select as typeof Select & {
  ComboBox: typeof ComboBox;
  Item: typeof SelectItem;
};
_Select.ComboBox = ComboBox;
_Select.Item = SelectItem;

export { _Select as Select };
